import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './BottomSheet.scss';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function BottomSheet({ isOpen, onClose, children }: BottomSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentDragY, setCurrentDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Fixed px, not a percentage of sheet height: keeps the dismiss gesture feeling
  // consistent regardless of how much content the sheet holds.
  const CLOSE_THRESHOLD = 100;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setDragStartY(e.touches[0].clientY);
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    const deltaY = e.touches[0].clientY - dragStartY;
    setCurrentDragY(Math.max(0, deltaY));
  }, [isDragging, dragStartY]);

  const handleTouchEnd = useCallback(() => {
    if (currentDragY > CLOSE_THRESHOLD) {
      onClose();
    }
    setIsDragging(false);
    setCurrentDragY(0);
  }, [currentDragY, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDialogElement>) => {
    // A click on ::backdrop is dispatched on the dialog itself, so compare against its box.
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const outside = e.clientX < rect.left || e.clientX > rect.right
      || e.clientY < rect.top || e.clientY > rect.bottom;
    if (outside) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!isOpen) {
      if (dialog.open) dialog.close();
      return;
    }

    if (!dialog.open) dialog.showModal();
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Portals are unsupported by the server renderer.
  if (typeof document === 'undefined') return null;

  return createPortal(
    // Backdrop click is a mouse-only shortcut: the native dialog already closes on Escape through onClose.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className={`bottom-sheet${isDragging ? ' bottom-sheet--dragging' : ''}`}
      style={currentDragY > 0 ? { transform: `translateY(${currentDragY}px)` } : undefined}
      onClose={onClose}
      onClick={handleBackdropClick}
    >
      <div
        className="bottom-sheet__handle-zone"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="bottom-sheet__handle" />
      </div>
      <div className="bottom-sheet__content">
        {children}
      </div>
    </dialog>,
    document.body
  );
}

export default BottomSheet;
